import {
  Box,
  Flex,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiShield } from "react-icons/fi"

import { OrganisationsService, UsersService } from "@/client"
import type { ResourcePermissionRead, UserRole } from "@/client"
import { UserOrganizationsList } from "@/components/Users/UserOrganizationsList"
import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
} from "@/components/VisionUI"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/user-permissions/$userId")({
  component: UserPermissions,
})

interface FormData {
  organization_id: string
  role: UserRole | "none"
}

const roleKeys: {
  value: UserRole | "none"
  labelKey: string
  descKey: string
}[] = [
  {
    value: "none",
    labelKey: "admin:permissions.roles.none",
    descKey: "admin:permissions.roles.noneDesc",
  },
  {
    value: "admin",
    labelKey: "admin:permissions.roles.admin",
    descKey: "admin:permissions.roles.adminDesc",
  },
  {
    value: "operator",
    labelKey: "admin:permissions.roles.operator",
    descKey: "admin:permissions.roles.operatorDesc",
  },
  {
    value: "readonly",
    labelKey: "admin:permissions.roles.readonly",
    descKey: "admin:permissions.roles.readonlyDesc",
  },
]

const getRoleFromPermissions = (
  permissions: ResourcePermissionRead[],
): UserRole | "none" => {
  if (!permissions || permissions.length === 0) return "none"

  // Check if user has admin permissions (full CRUD on organization/user resources)
  const hasAdminPerms = permissions.some(
    (p) =>
      (p.resource_type === "organisation" || p.resource_type === "user") &&
      p.permission >= 15, // 15 = 1111 in binary (all permissions)
  )
  if (hasAdminPerms) return "admin"

  // Check if user has write permissions on any resource
  const hasWritePerms = permissions.some((p) => p.permission & 2) // 2 = write permission
  if (hasWritePerms) return "operator"

  // Check if user has read permissions
  const hasReadPerms = permissions.some((p) => p.permission & 1) // 1 = read permission
  if (hasReadPerms) return "readonly"

  return "none"
}

function UserPermissions() {
  const { userId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const sc = useSemanticColors()
  const { t } = useTranslation(["admin", "common"])
  const roles = roleKeys.map((r) => ({
    value: r.value,
    label: t(r.labelKey),
    description: t(r.descKey),
  }))
  const [selectedOrg, setSelectedOrg] = useState<string>("")
  const [currentRole, setCurrentRole] = useState<UserRole | "none">("none")

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      role: "none",
    },
  })

  const watchedRole = watch("role")

  // Fetch user details
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => UsersService.readUser({ id: userId }),
  })

  // Fetch organizations
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["organizations"],
    queryFn: () =>
      OrganisationsService.readOrganisations({ skip: 0, limit: 100 }),
  })

  // Fetch current permissions for selected organization
  const { data: currentPermissions, isLoading: permissionsLoading } = useQuery({
    queryKey: ["user-org-permissions", userId, selectedOrg],
    queryFn: async () => {
      if (!selectedOrg) return []
      try {
        const permissions =
          await OrganisationsService.getOrganizationPermissions({
            organisationId: selectedOrg,
            userId: userId,
          })
        return permissions || []
      } catch (error) {
        return []
      }
    },
    enabled: !!selectedOrg,
  })

  // Update role when organization is selected
  useEffect(() => {
    if (
      selectedOrg &&
      currentPermissions !== undefined &&
      !permissionsLoading
    ) {
      const role = getRoleFromPermissions(currentPermissions)
      setCurrentRole(role)
      setValue("role", role)
    } else if (
      selectedOrg &&
      (currentPermissions === undefined || permissionsLoading)
    ) {
      // Still loading, don't update yet
    } else if (!selectedOrg) {
      // No org selected, reset to none
      setCurrentRole("none")
      setValue("role", "none")
    }
  }, [selectedOrg, currentPermissions, permissionsLoading, setValue])

  const setupPermissionsMutation = useMutation({
    mutationFn: async ({
      organization_id,
      role,
    }: { organization_id: string; role: UserRole | "none" }) => {
      if (role === "none") {
        // Remove all permissions for this user in the organization
        // First get all permissions to delete them
        const permissions =
          await OrganisationsService.getOrganizationPermissions({
            organisationId: organization_id,
            userId: userId,
          })

        // Delete each permission
        for (const perm of permissions || []) {
          await OrganisationsService.deletePermission({
            organisationId: organization_id,
            userId: userId,
            resourceType: perm.resource_type,
            resourceId: perm.resource_id || undefined,
          })
        }
      } else {
        // Set permissions
        await OrganisationsService.setupUserPermissions({
          organisationId: organization_id,
          requestBody: { user_id: userId, role },
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-org-permissions"] })
      showToast.showSuccessToast(t("admin:permissions.updated"))
      navigate({ to: "/user-management" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update permissions"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleFormSubmit = (data: FormData) => {
    setupPermissionsMutation.mutate({
      organization_id: data.organization_id,
      role: data.role,
    })
  }

  const selectedRoleInfo = roles.find((r) => r.value === watchedRole)

  if (userLoading || orgsLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  if (!user) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          User Not Found
        </Heading>
        <Text color={sc.textMuted}>The requested user could not be found.</Text>
      </VisionCard>
    )
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={8}>
        <Box flex={1}>
          <Heading size="xl" color={sc.heading}>
            {t("admin:permissions.title")}
          </Heading>
          <Text color={sc.textMuted} mt={1}>
            {t("admin:permissions.subtitle", {
              name: user.full_name || user.email,
            })}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/user-management" })}
          size="sm"
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Flex gap={6} direction={{ base: "column", lg: "row" }}>
        {/* User Info Card */}
        <Box flex="1" maxW={{ lg: "400px" }}>
          <VisionCard variant="gradient">
            <Flex align="center" gap={3} mb={6}>
              <IconBox variant="glass" size="md">
                <FiShield fontSize="20px" color={sc.iconColor} />
              </IconBox>
              <Box>
                <Heading size="md" color={sc.heading}>
                  {t("admin:permissions.userInfo")}
                </Heading>
              </Box>
            </Flex>

            <VStack align="start" gap={3}>
              <Box>
                <Text color={sc.textMuted} fontSize="sm">
                  {t("admin:permissions.fullName")}
                </Text>
                <Text color={sc.heading} fontWeight="500">
                  {user.full_name || t("admin:permissions.notProvided")}
                </Text>
              </Box>
              <Box>
                <Text color={sc.textMuted} fontSize="sm">
                  {t("admin:permissions.email")}
                </Text>
                <Text color={sc.heading} fontWeight="500">
                  {user.email}
                </Text>
              </Box>
              <Box>
                <Text color={sc.textMuted} fontSize="sm">
                  {t("admin:permissions.status")}
                </Text>
                <Text
                  color={user.is_active ? "green.400" : "gray.400"}
                  fontWeight="500"
                >
                  {user.is_active
                    ? t("common:status.active")
                    : t("common:status.inactive")}
                </Text>
              </Box>
              {user.is_superuser && (
                <Box
                  p={3}
                  borderRadius="12px"
                  bg={{ base: "gray.100", _dark: "rgba(159, 122, 234, 0.2)" }}
                  border={{
                    base: "1px solid",
                    _dark: "1px solid rgba(159, 122, 234, 0.4)",
                  }}
                  borderColor={{
                    base: "gray.300",
                    _dark: "rgba(159, 122, 234, 0.4)",
                  }}
                  w="100%"
                >
                  <Text
                    color={{ base: "gray.800", _dark: "purple.200" }}
                    fontSize="sm"
                    fontWeight="500"
                  >
                    {t("admin:permissions.superuserAccount")}
                  </Text>
                  <Text
                    color={{ base: "gray.600", _dark: "purple.100" }}
                    fontSize="xs"
                    mt={1}
                  >
                    {t("admin:permissions.superuserDesc")}
                  </Text>
                </Box>
              )}
            </VStack>

            <Box mt={6}>
              <Text color={sc.textMuted} fontSize="sm" fontWeight="500" mb={3}>
                {t("admin:permissions.currentMemberships")}
              </Text>
              <UserOrganizationsList user={user} />
            </Box>
          </VisionCard>
        </Box>

        {/* Permission Assignment Form */}
        <Box flex="2">
          <GradientBorder borderRadius="20px" p={0}>
            <VisionCard variant="elevated" p={6} borderRadius="20px">
              <Heading size="lg" color={sc.heading} mb={6}>
                {t("admin:permissions.changePermissions")}
              </Heading>

              <VStack
                gap={6}
                as="form"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <VStack w="100%" gap={4}>
                  <Field
                    required
                    invalid={!!errors.organization_id}
                    errorText={errors.organization_id?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("common:org.label")}
                    </Text>
                    <Box
                      as="select"
                      {...register("organization_id", {
                        required: "Organization is required",
                      })}
                      p={3}
                      borderRadius="12px"
                      bg="rgba(0, 0, 0, 0.3)"
                      backdropFilter="blur(20px)"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      w="100%"
                      transition="all 0.2s"
                      onChange={(e) => {
                        const value = (e.target as HTMLSelectElement).value
                        setSelectedOrg(value)
                        // Reset role when changing org
                        if (!value) {
                          setCurrentRole("none")
                          setValue("role", "none")
                        }
                      }}
                      _hover={{
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        bg: "rgba(0, 0, 0, 0.4)",
                      }}
                      _focus={{
                        outline: "none",
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                        bg: "rgba(0, 0, 0, 0.5)",
                      }}
                      css={{
                        "& option": {
                          background: "#1a1a1a",
                          color: "white",
                        },
                      }}
                    >
                      <option value="">
                        {t("admin:permissions.selectOrg")}
                      </option>
                      {organizations.map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name}
                        </option>
                      ))}
                    </Box>
                  </Field>

                  {selectedOrg && (
                    <>
                      <Box
                        w="100%"
                        p={3}
                        borderRadius="12px"
                        bg="rgba(52, 161, 221, 0.05)"
                        border="1px solid rgba(52, 161, 221, 0.2)"
                      >
                        <Text color="brand.200" fontSize="xs" fontWeight="500">
                          {t("admin:permissions.currentLevel")}
                        </Text>
                        {permissionsLoading ? (
                          <Spinner size="sm" color={sc.spinnerColor} mt={1} />
                        ) : (
                          <Text
                            color={sc.heading}
                            fontSize="sm"
                            fontWeight="600"
                            mt={1}
                          >
                            {roles.find((r) => r.value === currentRole)
                              ?.label || t("admin:permissions.roles.none")}
                          </Text>
                        )}
                      </Box>

                      <Field
                        required
                        invalid={!!errors.role}
                        errorText={errors.role?.message}
                      >
                        <Text
                          color={sc.heading}
                          fontSize="sm"
                          fontWeight="500"
                          mb={2}
                        >
                          {t("admin:permissions.newLevel")}
                        </Text>
                        <Box
                          as="select"
                          {...register("role", {
                            required: "Role is required",
                            value: currentRole,
                          })}
                          p={3}
                          borderRadius="12px"
                          bg="rgba(0, 0, 0, 0.3)"
                          backdropFilter="blur(20px)"
                          border="1px solid rgba(255, 255, 255, 0.2)"
                          color={sc.heading}
                          fontSize="sm"
                          fontWeight="500"
                          w="100%"
                          transition="all 0.2s"
                          _hover={{
                            borderColor: "rgba(255, 255, 255, 0.3)",
                            bg: "rgba(0, 0, 0, 0.4)",
                          }}
                          _focus={{
                            outline: "none",
                            borderColor: "brand.200",
                            boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                            bg: "rgba(0, 0, 0, 0.5)",
                          }}
                          css={{
                            "& option": {
                              background: "#1a1a1a",
                              color: "white",
                            },
                          }}
                        >
                          {roles.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </Box>
                      </Field>

                      {selectedRoleInfo && (
                        <Box
                          w="100%"
                          p={4}
                          borderRadius="12px"
                          bg={
                            selectedRoleInfo.value === "none"
                              ? "rgba(255, 0, 0, 0.1)"
                              : "rgba(52, 161, 221, 0.1)"
                          }
                          border={
                            selectedRoleInfo.value === "none"
                              ? "1px solid rgba(255, 0, 0, 0.3)"
                              : "1px solid rgba(52, 161, 221, 0.3)"
                          }
                        >
                          <Text
                            color={
                              selectedRoleInfo.value === "none"
                                ? "red.300"
                                : "brand.200"
                            }
                            fontSize="sm"
                            fontWeight="600"
                            mb={1}
                          >
                            {selectedRoleInfo.label}
                          </Text>
                          <Text color={sc.textSecondary} fontSize="sm">
                            {selectedRoleInfo.description}
                          </Text>
                        </Box>
                      )}
                    </>
                  )}
                </VStack>

                <HStack gap={3} w="100%">
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate({ to: "/user-management" })}
                  >
                    {t("common:actions.cancel")}
                  </VisionButton>
                  <VisionButton
                    type="submit"
                    variant="brand"
                    size="lg"
                    flex="1"
                    loading={setupPermissionsMutation.isPending}
                    disabled={!selectedOrg}
                  >
                    {setupPermissionsMutation.isPending
                      ? t("common:status.updating")
                      : t("admin:permissions.updatePermissions")}
                  </VisionButton>
                </HStack>
              </VStack>
            </VisionCard>
          </GradientBorder>
        </Box>
      </Flex>
    </Box>
  )
}
